import { batch } from 'react-redux';
import { EmeraldError, EmeraldErrorModal } from 'src/common/api/emerald-error';
import { withSpinner } from 'src/common/components/with-spinner/with-spinner';
import { popEmeraldModal } from 'src/common/emerald-modal-manager/emerald-modal-manager';
import { EmeraldDialogTypes } from 'src/common/enumerations/emerald-dialog-types';
import { IItemLookup } from 'src/common/interfaces/dtos/IItemLookup';
import { MiddleAreaActions } from 'src/common/redux/reducers/middle-area-reducer';
import { POSModeActions } from 'src/common/redux/reducers/pos-mode-reducer';
import { voidActiveTransaction } from 'src/home-page/business-logic/void-transaction';
import { initiateTransaction } from 'src/items-list/business-logic/initiate-transaction';
import { popupMessage } from 'src/messages/business-logic/popup-message';
import { ActivityType } from 'src/operation-notify/enum/activities.enum';
import {
  addInputData,
  onQuantityClick,
} from 'src/sales-keypad/business-logic/handle-sales-keypad-actions';
import { resetSalesKeypadState } from 'src/sales-keypad/business-logic/sales-keypad-operations';
import { initiateTendering } from 'src/tendering/business-logic/initiate-tendering';
import { ReturnsType } from 'src/transaction-search/interfaces/get-effective-return-policy-response';
import { restorePage } from '../../common/components/emerald-home-page/emerald-home-page';
import { store } from '../../common/redux/configure-store';
import { BottleReturnActions } from '../../common/redux/reducers/bottle-return-response-reducer';
import { IBottleDepositProps, showBottleDepositContainer } from '../component/bottle-deposit';
import { showBottleDepositQuantityDialog } from '../component/bottle-deposit-quantity-modal/bottle-deposit-quantity-modal';
import { getBottleReturnItems } from './service/bottle-deposit';

export const initiateBottleReturnTransaction = async (): Promise<void> => {
  await initiateTransaction({ typeCode: ActivityType.BottleDepositReturn })
    .then(() => store.dispatch(POSModeActions.setReturnState(ReturnsType.BottleDepositReturn)))
    .catch(popupMessage);
};

const closeBottleDepositWindow = async (): Promise<void> => {
  return voidActiveTransaction().then(() => {
    restorePage();
    batch(
      async (): Promise<void> => {
        store.dispatch(BottleReturnActions.resetState());
        store.dispatch(MiddleAreaActions.setMiddleAreaState({ tabValue: 1 }));
      },
    );
  });
};

const onItemCodeReceived = (itemCode: string): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    const { itemList } = store.getState().bottleReturn;
    const selectedItem: IItemLookup | undefined = itemList.find(item => item.itemId === itemCode);
    const modalId = 'BOTTLE_QUANTITY_MODAL';

    const onApprove = async (quantity: string): Promise<void> => {
      if (!quantity || quantity === '0') {
        popupMessage(new EmeraldError('SCO_QuantityItemHandledByShopper_ErrorQuantity'));
      } else {
        if (selectedItem?.regularSalesUnitPrice) {
          selectedItem.regularSalesUnitPrice.quantity = parseInt(quantity);
        }
        onQuantityClick(quantity);
        return addInputData(itemCode, parseInt(quantity))
          .then(resolve)
          .catch(reject)
          .finally(() => {
            resetSalesKeypadState();
            popEmeraldModal(modalId);
          });
      }
    };

    const onClose = () => {
      popEmeraldModal(modalId);
      resolve();
    };

    if (selectedItem) {
      store.dispatch(BottleReturnActions.setBottleReturnSelectedItemState(selectedItem));
      showBottleDepositQuantityDialog(
        {
          onClose,
          onApprove,
          selectedItem,
        },
        modalId,
      );
    } else {
      const errorModalMessage = new EmeraldErrorModal({
        message: 'NonBottleItem_ErrorHeader',
        type: EmeraldDialogTypes.Error,
        subMessage: 'NonBottleItem_ErrorSubHeader',
        acceptButtonLabel: 'retry',
        hideCancelButton: true,
      });
      popupMessage(errorModalMessage);
    }
  }).catch(popupMessage);

export const handleBottleDeposit = async (): Promise<void> =>
  //  Loading spinner modal while performing dispatch/Api calls. Post removing the spinner, will load the Bottle deposit screen with scanner callback
  withSpinner(() =>
    getBottleReturnItems()
      .then(items => store.dispatch(BottleReturnActions.setBottleReturnItemState(items)))
      .then(initiateBottleReturnTransaction),
  )
    .then(() => {
      const props: IBottleDepositProps = {
        onCancel: closeBottleDepositWindow,
        onItemCodeReceived,
        onFinish: initiateTendering,
      };
      showBottleDepositContainer(props, onItemCodeReceived);
    })
    .catch(popupMessage);
